import { Reveal, Overline } from "@/components/Reveal";
import { CheckCircle2, FileSearch, ClipboardList, Wrench, Activity, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadDialog from "@/components/LeadDialog";

const CPE_STEPS = [
  { icon: FileSearch, label: "Audit" },
  { icon: ClipboardList, label: "Étude" },
  { icon: Wrench, label: "Travaux" },
  { icon: Activity, label: "Suivi" },
  { icon: ShieldCheck, label: "Garanties" },
];

const CPE_POINTS = [
  { t: "Définition", d: "Un contrat par lequel nous nous engageons sur un niveau d'économies d'énergie précis et mesurable." },
  { t: "Fonctionnement", d: "Audit, plan d'actions, réalisation des travaux puis mesure des performances sur toute la durée du contrat." },
  { t: "Garanties de résultats", d: "Les économies sont contractuellement garanties : nos intérêts sont alignés avec les vôtres." },
  { t: "Mesure des performances", d: "Instrumentation et suivi continu des consommations, avec ajustements climatiques et d'usage." },
];

export default function CPE() {
  return (
    <section id="cpe" className="bg-white py-24 lg:py-32" data-testid="cpe-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <Overline>Le dispositif clé</Overline>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-midnight">
                Le Contrat de Performance Énergétique
              </h2>
              <p className="mt-5 text-slate-600 font-light leading-relaxed">
                Le CPE transforme une promesse d'économies en un engagement contractuel garanti,
                mesuré et suivi dans la durée.
              </p>
              <LeadDialog leadType="audit">
                <Button variant="outline" className="mt-8 rounded-none border-midnight text-midnight hover:bg-midnight hover:text-white group" data-testid="cpe-cta">
                  Étudier mon projet <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </LeadDialog>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {CPE_POINTS.map((p, i) => (
                <Reveal key={p.t} delay={i * 0.08} className="bg-white p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald" />
                    <h3 className="font-display text-lg font-bold text-midnight">{p.t}</h3>
                  </div>
                  <p className="text-slate-600 font-light text-sm leading-relaxed">{p.d}</p>
                </Reveal>
              ))}
            </div>

            {/* Process diagram */}
            <Reveal delay={0.1}>
              <div className="mt-10 bg-midnight text-white p-8 lg:p-10">
                <p className="overline text-lime mb-8">Le parcours d'un CPE</p>
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                  {CPE_STEPS.map((s, i) => (
                    <div key={s.label} className="flex items-center gap-4 md:flex-col md:gap-3 md:flex-1">
                      <div className="flex md:flex-col items-center gap-4 md:gap-3 md:flex-1">
                        <div className="h-14 w-14 border border-lime/40 bg-lime/10 flex items-center justify-center shrink-0">
                          <s.icon className="h-6 w-6 text-lime" strokeWidth={1.5} />
                        </div>
                        <span className="font-display font-bold">{s.label}</span>
                      </div>
                      {i < CPE_STEPS.length - 1 && (
                        <ArrowRight className="h-5 w-5 text-lime hidden md:block rotate-0 shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
